// Lateral-view (sagittal) feature extraction.
//
// Internal joint angles are used only to characterize *patterns* and derive
// categorical bands. No angle values are output. Outputs are normalized
// magnitudes / dimensionless indices, longitudinally comparable.

import { Joints, isotropicFrame, type PoseFrame, type Side } from "../pose/landmarks";
import type { EventResult, GaitCycle } from "./events";
import { angleAt, angleFromVertical, vec, percentile, normalizedJerk } from "./geometry";
import { savitzkyGolay } from "../pose/smoothing";
import { gateSeries } from "./robustness";
import { mean, std } from "./signal";

export interface SideMagnitude {
  L: number;
  R: number;
}

export interface LateralFeatures {
  /** Knee flexion excursion (range) per side, internal-degrees scale → magnitude. */
  kneeFlexionExcursion: SideMagnitude;
  /** Terminal knee extension deficit per side (higher = less extension). */
  terminalExtensionDeficit: SideMagnitude;
  /** Hip extension limitation per side (higher = less terminal extension). */
  hipExtensionLimitation: SideMagnitude;
  /** Forward trunk lean (deg-from-vertical, internal) → normalized magnitude. */
  trunkForwardLean: number;
  /** Stride-length proxy (normalized AP foot excursion), mean. */
  strideLength: number;
  /** Stride regularity: CV of per-cycle stride proxies (lower = better). */
  strideRegularityCV: number;
  /** Gait smoothness (normalized jerk of pelvis trajectory; lower = smoother). */
  smoothnessJerk: number;
}

export function computeLateralFeatures(
  frames: PoseFrame[],
  events: EventResult,
  aspect = 1,
): LateralFeatures {
  // Work in isotropic image space so angles/distances aren't aspect-distorted.
  // aspect = 1 reproduces the previous (raw-coordinate) behaviour exactly.
  const fr = frames.map((f) => isotropicFrame(f, aspect));
  const ts = fr.map((f) => f.tMs);
  const dt = ts.length > 1 ? (ts[ts.length - 1] - ts[0]) / (ts.length - 1) : 16.7;

  const kneeFlexionExcursion: SideMagnitude = { L: 0, R: 0 };
  const terminalExtensionDeficit: SideMagnitude = { L: 0, R: 0 };
  const hipExtensionLimitation: SideMagnitude = { L: 0, R: 0 };

  for (const side of ["L", "R"] as Side[]) {
    // Visibility gate: drop low-confidence frames out of the angle series before
    // smoothing so an occluded far-leg sample can't distort the percentiles.
    const kneeVis = fr.map((f) => {
      const j = new Joints(f);
      return Math.min(j.hip(side).visibility, j.knee(side).visibility, j.ankle(side).visibility);
    });
    // Knee flexion angle proxy: 180° = fully extended; smaller = more flexed.
    // We work with flexion = 180 - interior angle so larger = more flexed.
    const kneeFlex = savitzkyGolay(
      gateSeries(
        fr.map((f) => {
          const j = new Joints(f);
          return 180 - angleAt(j.hip(side), j.knee(side), j.ankle(side));
        }),
        kneeVis,
      ),
      7,
    );
    const kneeMax = percentile(kneeFlex, 95);
    const kneeMin = percentile(kneeFlex, 5);
    kneeFlexionExcursion[side] = Math.max(0, kneeMax - kneeMin);
    // Terminal extension: how flexed the knee remains at its straightest point.
    // Higher residual flexion = bigger extension deficit.
    terminalExtensionDeficit[side] = Math.max(0, kneeMin);

    // Hip extension: angle between trunk and thigh; limited terminal extension
    // shows as a smaller achievable extension. We use flexion = 180 - angle and
    // take the minimum (most extended); higher minimum = more limitation.
    const hipVis = fr.map((f) => {
      const j = new Joints(f);
      return Math.min(
        j.shoulderCenter().visibility,
        j.hip(side).visibility,
        j.knee(side).visibility,
      );
    });
    const hipFlex = savitzkyGolay(
      gateSeries(
        fr.map((f) => {
          const j = new Joints(f);
          return 180 - angleAt(j.shoulderCenter(), j.hip(side), j.knee(side));
        }),
        hipVis,
      ),
      7,
    );
    hipExtensionLimitation[side] = Math.max(0, percentile(hipFlex, 5));
  }

  // Forward trunk lean: trunk axis (pelvis→shoulder) angle from vertical.
  const trunkVis = fr.map((f) => {
    const j = new Joints(f);
    return Math.min(j.pelvisCenter().visibility, j.shoulderCenter().visibility);
  });
  const trunkLeanDeg = gateSeries(
    fr.map((f) => {
      const j = new Joints(f);
      return angleFromVertical(vec(j.pelvisCenter(), j.shoulderCenter()));
    }),
    trunkVis,
  );
  const trunkForwardLean = percentile(trunkLeanDeg, 75);

  // Stride proxy per cycle: AP foot excursion (max-min of foot-vs-pelvis x).
  const strideProxies = cycleStrideProxies(fr, events.cycles);
  const strideLength = strideProxies.length ? mean(strideProxies) : 0;
  const strideRegularityCV =
    strideProxies.length > 1 ? std(strideProxies) / (mean(strideProxies) || 1) : 0;

  // Smoothness: normalized jerk of pelvis horizontal trajectory (gated).
  const pelvisVis = fr.map((f) => new Joints(f).pelvisCenter().visibility);
  const pelvisX = gateSeries(fr.map((f) => new Joints(f).pelvisCenter().x), pelvisVis);
  const smoothnessJerk = normalizedJerk(pelvisX, dt);

  return {
    kneeFlexionExcursion,
    terminalExtensionDeficit,
    hipExtensionLimitation,
    trunkForwardLean,
    strideLength,
    strideRegularityCV,
    smoothnessJerk,
  };
}

function cycleStrideProxies(frames: PoseFrame[], cycles: GaitCycle[]): number[] {
  const out: number[] = [];
  for (const c of cycles) {
    const seg = frames.filter((f) => f.tMs >= c.startMs && f.tMs <= c.endMs);
    if (seg.length < 3) continue;
    const apVis = seg.map((f) => {
      const j = new Joints(f);
      return Math.min(j.footIndex(c.side).visibility, j.pelvisCenter().visibility);
    });
    const ap = gateSeries(
      seg.map((f) => {
        const j = new Joints(f);
        return j.footIndex(c.side).x - j.pelvisCenter().x;
      }),
      apVis,
    );
    out.push(Math.max(...ap) - Math.min(...ap));
  }
  return out;
}
